// src/pages/Courses/Courses.js
import React, { useState, useMemo } from 'react';
import './Courses.css';
import {
  courseData,
  centersData,
  levels,
  getCoursesByCentre,
  getCourseCountByCentre
} from '../../data/CoursesData';

const Courses = () => {
  // ============ VIEW MODE ============
  const [viewMode, setViewMode] = useState('all');

  // ============ FILTER STATES ============
  const [selectedCategory, setSelectedCategory] = useState('All Categories');
  const [selectedLevel, setSelectedLevel] = useState('All Levels');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('name');

  // ============ MODAL STATE ============
  const [selectedCourse, setSelectedCourse] = useState(null);

  // ============ SCOPED COURSES ============
  const scopedCourses = useMemo(() => {
    if (viewMode === 'all') return courseData;
    return getCoursesByCentre(viewMode);
  }, [viewMode]);

  // ============ FILTERED COURSES ============
  const filteredCourses = useMemo(() => {
    let filtered = [...scopedCourses];

    if (selectedCategory !== 'All Categories') {
      filtered = filtered.filter(c => c.category === selectedCategory);
    }
    if (selectedLevel !== 'All Levels') {
      filtered = filtered.filter(c => c.level === selectedLevel);
    }
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        c =>
          c.name.toLowerCase().includes(term) ||
          c.category.toLowerCase().includes(term) ||
          (c.outline && c.outline.some(o => o.toLowerCase().includes(term)))
      );
    }

    switch (sortBy) {
      case 'name':
        filtered.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case 'fee-low':
        filtered.sort(
          (a, b) => parseFloat(a.fee.replace(/,/g, '')) - parseFloat(b.fee.replace(/,/g, ''))
        );
        break;
      case 'fee-high':
        filtered.sort(
          (a, b) => parseFloat(b.fee.replace(/,/g, '')) - parseFloat(a.fee.replace(/,/g, ''))
        );
        break;
      case 'duration':
        filtered.sort((a, b) => {
          const parseDur = d => {
            if (d.includes('Year')) return parseInt(d) * 48;
            if (d.includes('Month')) return parseInt(d) * 4;
            return parseInt(d);
          };
          return parseDur(a.duration) - parseDur(b.duration);
        });
        break;
      case 'popular':
        filtered.sort((a, b) => (b.popular ? 1 : 0) - (a.popular ? 1 : 0));
        break;
      default:
        break;
    }
    return filtered;
  }, [scopedCourses, selectedCategory, selectedLevel, searchTerm, sortBy]);

  // ============ SCOPED CATEGORIES ============
  const scopedCategories = useMemo(() => {
    const cats = [...new Set(scopedCourses.map(c => c.category))];
    return ['All Categories', ...cats];
  }, [scopedCourses]);

  const getBadgeClass = level => {
    switch (level) {
      case 'Beginner': return 'course-badge-beginner';
      case 'Intermediate': return 'course-badge-intermediate';
      case 'Advanced': return 'course-badge-advanced';
      default: return '';
    }
  };

  const currentCenter = viewMode !== 'all' ? centersData.find(c => c.id === viewMode) : null;

  // ============ RENDER ============
  return (
    <div className="courses-page">
      {/* HERO */}
      <div className="courses-hero">
        <div className="courses-hero-content">
          <div className="hero-badge">🏆 PITAC — Skills for Today, Opportunities for Tomorrow</div>
          <h1>
            {currentCenter ? (
              <>Courses at <span>{currentCenter.shortName}</span></>
            ) : (
              <>Discover Our <span>Training Programs</span></>
            )}
          </h1>
          <p>
            {currentCenter
              ? `Explore ${getCourseCountByCentre(currentCenter.id)} courses available at ${currentCenter.name}`
              : `Explore ${courseData.length}+ comprehensive training programs across ${centersData.length} centres nationwide`}
          </p>
          <div className="hero-stats">
            <div className="hero-stat">
              <strong>{viewMode === 'all' ? courseData.length : getCourseCountByCentre(viewMode)}+</strong>
              <span>Courses</span>
            </div>
            <div className="hero-stat">
              <strong>100%</strong>
              <span>Certified</span>
            </div>
            <div className="hero-stat">
              <strong>{centersData.length}</strong>
              <span>Centres</span>
            </div>
          </div>
        </div>
      </div>

      <div className="courses-container">
        {/* ============ CENTRE TABS — SINGLE ROW, FULL TEXT ============ */}
        <div className="centre-tabs-section">
          <div className="centre-tabs-header">
            <h3>🏢 Browse by Centre</h3>
            <p>Select a centre to see only its courses, or view all combined</p>
          </div>

          <div className="centre-tabs">
            <button
              className={`centre-tab centre-tab-all ${viewMode === 'all' ? 'active' : ''}`}
              onClick={() => {
                setViewMode('all');
                setSelectedCategory('All Categories');
                setSelectedLevel('All Levels');
              }}
            >
              <span className="centre-tab-icon">🌐</span>
              <div className="centre-tab-info">
                <strong>All Centres</strong>
                <small>Combined view</small>
              </div>
              <span className="centre-tab-count">{courseData.length}</span>
            </button>

            {centersData.map(center => {
              const count = getCourseCountByCentre(center.id);
              return (
                <button
                  key={center.id}
                  className={`centre-tab ${viewMode === center.id ? 'active' : ''} ${
                    center.type === 'head-office' ? 'centre-tab-head' : ''
                  }`}
                  onClick={() => {
                    setViewMode(center.id);
                    setSelectedCategory('All Categories');
                    setSelectedLevel('All Levels');
                  }}
                >
                  <span className="centre-tab-icon">{center.emoji}</span>
                  <div className="centre-tab-info">
                    <strong>{center.shortName}</strong>
                    <small>{center.province}</small>
                  </div>
                  <span className="centre-tab-count">{count}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* ACTIVE CENTRE BANNER */}
        {currentCenter && (
          <div className="active-centre-banner" style={{ background: currentCenter.gradient }}>
            <div className="active-centre-icon">{currentCenter.emoji}</div>
            <div className="active-centre-info">
              <span className="active-centre-tag">{currentCenter.provinceCode}</span>
              <h3>{currentCenter.name}</h3>
              <p>📍 {currentCenter.address}</p>
              <div className="active-centre-contacts">
                <span>📞 {currentCenter.phone}</span>
                {currentCenter.phone2 && <span>📞 {currentCenter.phone2}</span>}
                <span>✉️ {currentCenter.email}</span>
              </div>
            </div>
            <div className="active-centre-actions">
              <a href={currentCenter.mapUrl} target="_blank" rel="noopener noreferrer" className="active-centre-btn">
                🗺️ Map
              </a>
              <a href={`tel:${currentCenter.phone.replace(/[^0-9+]/g, '')}`} className="active-centre-btn active-centre-btn-gold">
                📞 Call
              </a>
            </div>
          </div>
        )}

        {/* FILTERS */}
        <div className="courses-filters">
          <div className="search-box">
            <input
              type="text"
              placeholder="🔍 Search courses, categories, or skills..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>

          <div className="filter-group">
            <select
              value={selectedCategory}
              onChange={e => setSelectedCategory(e.target.value)}
              className="filter-select"
            >
              {scopedCategories.map(c => <option key={c} value={c}>{c}</option>)}
            </select>

            <select
              value={selectedLevel}
              onChange={e => setSelectedLevel(e.target.value)}
              className="filter-select"
            >
              {levels.map(l => <option key={l} value={l}>{l}</option>)}
            </select>

            <select
              value={sortBy}
              onChange={e => setSortBy(e.target.value)}
              className="filter-select"
            >
              <option value="name">Sort by Name</option>
              <option value="fee-low">Fee: Low to High</option>
              <option value="fee-high">Fee: High to Low</option>
              <option value="duration">Sort by Duration</option>
              <option value="popular">Most Popular</option>
            </select>
          </div>
        </div>

        {/* STATS */}
        <div className="courses-stats">
          <span>
            📚 Showing <strong>{filteredCourses.length}</strong> of{' '}
            {scopedCourses.length} courses
            {currentCenter && (<> at <strong>{currentCenter.shortName}</strong></>)}
          </span>
        </div>

        {/* COURSE GRID */}
        <div className="courses-grid">
          {filteredCourses.map(course => {
            const center = centersData.find(c => c.id === course.centerId);
            return (
              <div
                key={course.id}
                className="course-card"
                onClick={() => setSelectedCourse(course)}
              >
                {course.popular && <div className="popular-ribbon">⭐ Popular</div>}

                <div className="course-card-header">
                  <span className={`course-badge ${getBadgeClass(course.level)}`}>
                    {course.level}
                  </span>
                  <span className="course-category">{course.category}</span>
                </div>

                <div className="course-icon-large">{course.icon}</div>
                <h3 className="course-name">{course.name}</h3>

                <div className="course-center-tag">
                  <span>{center?.emoji}</span>
                  <span>{center?.shortName}</span>
                </div>

                <div className="course-details">
                  <div className="course-detail">
                    <span className="detail-icon">⏱️</span>
                    <span>{course.duration}</span>
                  </div>
                  <div className="course-detail">
                    <span className="detail-icon">💰</span>
                    <span className="course-fee">Rs. {course.fee}</span>
                  </div>
                </div>

                {course.certification && (
                  <div className="cert-badge">🎓 {course.certification}</div>
                )}

                <button className="course-enroll-btn">View Details →</button>
              </div>
            );
          })}
        </div>

        {filteredCourses.length === 0 && (
          <div className="no-courses">
            <p>😕 No courses found matching your criteria.</p>
            <button
              className="reset-btn"
              onClick={() => {
                setSelectedCategory('All Categories');
                setSelectedLevel('All Levels');
                setSearchTerm('');
              }}
            >Reset Filters</button>
          </div>
        )}
      </div>

      {/* COURSE DETAIL MODAL */}
      {selectedCourse && (
        <div className="modal-overlay" onClick={() => setSelectedCourse(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setSelectedCourse(null)}>✕</button>
            <div className="modal-header">
              <div className="modal-icon">{selectedCourse.icon}</div>
              <div>
                <span className={`course-badge ${getBadgeClass(selectedCourse.level)}`}>
                  {selectedCourse.level}
                </span>
                <h2>{selectedCourse.name}</h2>
                <p className="modal-category">{selectedCourse.category}</p>
              </div>
            </div>

            <div className="modal-info">
              <div className="modal-info-item">
                <span>⏱️</span>
                <div>
                  <small>Duration</small>
                  <strong>{selectedCourse.duration}</strong>
                </div>
              </div>
              <div className="modal-info-item">
                <span>💰</span>
                <div>
                  <small>Fee</small>
                  <strong>Rs. {selectedCourse.fee}</strong>
                </div>
              </div>
              <div className="modal-info-item">
                <span>📍</span>
                <div>
                  <small>Centre</small>
                  <strong>
                    {centersData.find(c => c.id === selectedCourse.centerId)?.shortName}
                  </strong>
                </div>
              </div>
              {selectedCourse.certification && (
                <div className="modal-info-item">
                  <span>🎓</span>
                  <div>
                    <small>Certification</small>
                    <strong>{selectedCourse.certification}</strong>
                  </div>
                </div>
              )}
            </div>

            {selectedCourse.outline && (
              <div className="modal-outline">
                <h4>📋 Course Outline</h4>
                <ul>
                  {selectedCourse.outline.map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="modal-actions">
              <a
                href={`tel:${centersData
                  .find(c => c.id === selectedCourse.centerId)
                  ?.phone.replace(/[^0-9+]/g, '')}`}
                className="modal-btn modal-btn-outline"
              >
                📞 Call to Enroll
              </a>
              <button
                className="modal-btn modal-btn-primary"
                onClick={() => setSelectedCourse(null)}
              >
                ✕ Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Courses;